import { useEffect, useRef, useState } from 'react';
import SimpleReactValidator from 'simple-react-validator';
import AppService from "../../../services/app.service";
import Loader from '../../../components/loader';
import Layout from '../../../layout/Layout';

interface ClientOutreachSummaryDetail {
    id: number;
    accountId: number;
    accountName: string;
    accountType: string;
    simPlayCountLast90d: number;
    usageTier: string;
    usageDataTier: string | null;
    usageFeedbackTier: string | null;
    editStatus: string;
    summary: string;
}

const UpdateClientOutreachSummary = (props) => {

    const [formState, setFormState] = useState<ClientOutreachSummaryDetail>({
        id: 0,
        accountId: 0,
        accountName: '',
        accountType: '',
        simPlayCountLast90d: 0,
        usageTier: '',
        usageDataTier: '',
        usageFeedbackTier: '',
        editStatus: 'Default',
        summary: ''
    });
    const [loading, setLoading] = useState(false);
    const [originalSummary, setOriginalSummary] = useState('');
    const [characterCount, setCharacterCount] = useState(0);
    const [, forceUpdate] = useState(0);
    
    const MAX_CHARACTERS = 5000;
    const simpleValidator = useRef(new SimpleReactValidator({ 
        autoForceUpdate: { forceUpdate: forceUpdate } 
    }));

    const summaryId = props['match'].params.id;

    // Check if summary has actual text content
    const isSummaryEmpty = () => {
        return !formState.summary.trim();
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const response = await AppService.get(summaryId, 'client-outreach/summary');
            if (response) {
                const summaryData = response.data || response;
                const loadedSummary = summaryData.summary || '';
                
                // Calculate initial character count
                setCharacterCount(loadedSummary.length);
                
                setFormState({
                    id: summaryData.id,
                    accountId: summaryData.accountId,
                    accountName: summaryData.accountName,
                    accountType: summaryData.accountType,
                    simPlayCountLast90d: summaryData.simPlayCountLast90d,
                    usageTier: summaryData.usageTier || '',
                    usageDataTier: summaryData.usageDataTier || '',
                    usageFeedbackTier: summaryData.usageFeedbackTier || '',
                    editStatus: summaryData.editStatus,
                    summary: loadedSummary
                });
                setOriginalSummary(loadedSummary);
            }
        } catch (error) {
            console.error('Error loading client outreach summary:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (summaryId) {
            loadData();
        }
    }, [summaryId]);

    const handleSummaryChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const content = e.target.value;
        const charCount = content.length;
        
        // Always update character count
        setCharacterCount(charCount);
        
        // Only update content if within character limit
        if (charCount <= MAX_CHARACTERS) {
            setFormState(prevState => ({ ...prevState, summary: content }));
        }
    }

    const submitForm = async (e) => {
        e.preventDefault();
        
        // Check if summary has actual content
        if (isSummaryEmpty()) {
            simpleValidator.current.showMessages();
            forceUpdate(prev => prev + 1); // Force re-render to show validation message
            return;
        }
        
        if (simpleValidator.current.allValid()) {
            setLoading(true);
            try {
                // Trim the summary to remove leading/trailing whitespace
                const trimmedSummary = formState.summary.trim();
                
                // Check if summary has changed
                if (trimmedSummary === originalSummary) {
                    setLoading(false);
                    return; // Don't submit if no changes
                }
                
                const requestBody = { summary: trimmedSummary };
                
                const response = await AppService.awsURL(
                    requestBody,
                    `client-outreach/summary/${summaryId}`,
                    'PATCH',
                    'application/json'
                );
                if (response.status === 200) {
                    props.history.push('/client-outreach-summaries');
                }
            } catch (error) {
                console.error('Error updating client outreach summary:', error);
            } finally {
                setLoading(false);
            }
        } else {
            simpleValidator.current.showMessages();
        }
    };

    const discardForm = () => {
        props.history.push('/client-outreach-summaries');
    }

    // Check if summary has changed (compare trimmed versions to ignore trailing spaces)
    const isSummaryChanged = formState.summary.trim() !== originalSummary;

    return (
        <div>
            <Layout
                title="Client Outreach Summary Management"
                history={props.history}
                titleUrl="/client-outreach-summaries"
                subTitle="Edit Summary"
                activeCOSLink={true}
                showBreadCrumb={true}
            >
                <section className="forms">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="card">
                                    <div className="card-header d-flex align-items-center">
                                        <h3 className="sub-heading">Edit Client Outreach Summary</h3>
                                    </div>
                                    <div className="card-body">
                                        <form className="form-horizontal" noValidate={true}>
                                            <div className="form-group row">
                                                <label className="col-sm-3 form-control-label label">Account Name</label>
                                                <div className="col-sm-9">
                                                    <input 
                                                        name="accountName" 
                                                        type="text" 
                                                        className="form-control fields-font" 
                                                        value={formState.accountName} 
                                                        readOnly 
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-group row">
                                                <label className="col-sm-3 form-control-label label">Account Type</label>
                                                <div className="col-sm-9">
                                                    <input 
                                                        name="accountType" 
                                                        type="text" 
                                                        className="form-control fields-font" 
                                                        value={formState.accountType} 
                                                        readOnly 
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-group row">
                                                <label className="col-sm-3 form-control-label label">Sim Play Count (Last 90 Days)</label>
                                                <div className="col-sm-9">
                                                    <input 
                                                        name="simPlayCountLast90d" 
                                                        type="number" 
                                                        className="form-control fields-font" 
                                                        value={formState.simPlayCountLast90d} 
                                                        readOnly 
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-group row">
                                                <label className="col-sm-3 form-control-label label">Usage Tier</label>
                                                <div className="col-sm-9">
                                                    <input 
                                                        name="usageTier" 
                                                        type="text" 
                                                        className="form-control fields-font" 
                                                        value={formState.usageTier || ''} 
                                                        readOnly 
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-group row">
                                                <label className="col-sm-3 form-control-label label">Usage Data Tier</label>
                                                <div className="col-sm-9">
                                                    <input 
                                                        name="usageDataTier" 
                                                        type="text" 
                                                        className="form-control fields-font" 
                                                        value={formState.usageDataTier || ''} 
                                                        readOnly 
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-group row">
                                                <label className="col-sm-3 form-control-label label">Usage Feedback Tier</label>
                                                <div className="col-sm-9">
                                                    <input 
                                                        name="usageFeedbackTier" 
                                                        type="text" 
                                                        className="form-control fields-font" 
                                                        value={formState.usageFeedbackTier || ''} 
                                                        readOnly 
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-group row">
                                                <label className="col-sm-3 form-control-label required label">Summary</label>
                                                <div className="col-sm-9">
                                                    <textarea 
                                                        className="form-control fields-font"
                                                        rows={10}
                                                        value={formState.summary}
                                                        onChange={handleSummaryChange}
                                                        placeholder="Enter summary..."
                                                        maxLength={MAX_CHARACTERS}
                                                    />
                                                    {characterCount >= MAX_CHARACTERS && (
                                                        <div className="mt-2">
                                                            <small className="text-danger">
                                                                Maximum character limit of {MAX_CHARACTERS} reached.
                                                            </small>
                                                        </div>
                                                    )}
                                                    {isSummaryEmpty() && (
                                                        <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                                            Summary cannot be empty.
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="form-group row">
                                                <div className="col-sm-9 offset-sm-3">
                                                    <button 
                                                        type='submit' 
                                                        onClick={submitForm} 
                                                        className="btn btn-submit float-right buttons"
                                                        disabled={!isSummaryChanged || loading || characterCount > MAX_CHARACTERS || isSummaryEmpty()}
                                                    >
                                                        Update Summary
                                                    </button>
                                                    <button 
                                                        type='button' 
                                                        className="btn btn-cancel float-right buttons" 
                                                        onClick={discardForm}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </Layout>
            {loading && <div className="overlay">
                <Loader loading={loading} />
            </div>}
        </div>
    );
}

export default UpdateClientOutreachSummary;